import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Check, Loader2, X, Sparkles, FileText, Package, Infinity as Inf, Star } from "lucide-react";
import { API } from "@/lib/constants";

// Copy — 6 languages
const L = {
  en: {
    title: "Get more CVs",
    sub: "Buy a one-off pack or unlock unlimited access.",
    packs_title: "One-off packs",
    lifetime_title: "Unlimited access",
    per_cv: "/ CV",
    one_time: "one-time",
    best_value: "MOST POPULAR",
    lifetime_badge: "BEST VALUE",
    features_packs: "Instant delivery · No subscription · Credits never expire",
    features_lifetime: ["Unlimited CV generations", "Priority AI (Claude Haiku 4.5)", "Full CV history + downloads", "All 6 languages", "Priority support"],
    buy: "Buy",
    go_lifetime: "Get Lifetime",
    loading: "Redirecting to Stripe…",
    or: "or",
    promo: "I have a promo code",
    cvs: "CVs",
    cv: "CV",
  },
  fr: {
    title: "Débloque plus de CV",
    sub: "Achète un pack à l'unité ou passe à l'illimité.",
    packs_title: "Packs à l'unité",
    lifetime_title: "Accès illimité",
    per_cv: "/ CV",
    one_time: "paiement unique",
    best_value: "LE PLUS POPULAIRE",
    lifetime_badge: "MEILLEUR CHOIX",
    features_packs: "Livraison immédiate · Sans abonnement · Crédits sans expiration",
    features_lifetime: ["CV illimités", "IA prioritaire (Claude Haiku 4.5)", "Historique complet + téléchargements", "Toutes les 6 langues", "Support prioritaire"],
    buy: "Acheter",
    go_lifetime: "Passer à vie",
    loading: "Redirection vers Stripe…",
    or: "ou",
    promo: "J'ai un code promo",
    cvs: "CV",
    cv: "CV",
  },
  de: {
    title: "Mehr CVs freischalten", sub: "Einzel-Pack kaufen oder unbegrenzten Zugriff erhalten.",
    packs_title: "Einzel-Packs", lifetime_title: "Unbegrenzter Zugriff",
    per_cv: "/ CV", one_time: "einmalig",
    best_value: "AM BELIEBTESTEN", lifetime_badge: "BESTES ANGEBOT",
    features_packs: "Sofortige Lieferung · Kein Abo · Guthaben ohne Ablauf",
    features_lifetime: ["Unbegrenzte CVs", "Prioritäre KI", "Vollständiger Verlauf", "Alle 6 Sprachen", "Prioritätssupport"],
    buy: "Kaufen", go_lifetime: "Lebenslang holen", loading: "Weiterleitung zu Stripe…",
    or: "oder", promo: "Ich habe einen Promo-Code", cvs: "CVs", cv: "CV",
  },
  it: {
    title: "Ottieni più CV", sub: "Compra un pacchetto singolo o passa all'illimitato.",
    packs_title: "Pacchetti singoli", lifetime_title: "Accesso illimitato",
    per_cv: "/ CV", one_time: "una tantum",
    best_value: "PIÙ POPOLARE", lifetime_badge: "MIGLIOR OFFERTA",
    features_packs: "Consegna immediata · Nessun abbonamento · Crediti senza scadenza",
    features_lifetime: ["CV illimitati", "IA prioritaria", "Cronologia completa", "Tutte le 6 lingue", "Supporto prioritario"],
    buy: "Compra", go_lifetime: "Passa a Vita", loading: "Reindirizzamento a Stripe…",
    or: "o", promo: "Ho un codice promo", cvs: "CV", cv: "CV",
  },
  es: {
    title: "Obtén más CVs", sub: "Compra un pack único o desbloquea acceso ilimitado.",
    packs_title: "Packs de un solo pago", lifetime_title: "Acceso ilimitado",
    per_cv: "/ CV", one_time: "pago único",
    best_value: "MÁS POPULAR", lifetime_badge: "MEJOR OFERTA",
    features_packs: "Entrega inmediata · Sin suscripción · Créditos sin caducidad",
    features_lifetime: ["CVs ilimitados", "IA prioritaria", "Historial completo", "Los 6 idiomas", "Soporte prioritario"],
    buy: "Comprar", go_lifetime: "Obtener de por vida", loading: "Redirigiendo a Stripe…",
    or: "o", promo: "Tengo un código promo", cvs: "CVs", cv: "CV",
  },
  pt: {
    title: "Obtenha mais CVs", sub: "Compra um pack único ou desbloqueia acesso ilimitado.",
    packs_title: "Packs únicos", lifetime_title: "Acesso ilimitado",
    per_cv: "/ CV", one_time: "pagamento único",
    best_value: "MAIS POPULAR", lifetime_badge: "MELHOR OFERTA",
    features_packs: "Entrega imediata · Sem subscrição · Créditos sem expiração",
    features_lifetime: ["CVs ilimitados", "IA prioritária", "Histórico completo", "Todas as 6 línguas", "Suporte prioritário"],
    buy: "Comprar", go_lifetime: "Obter Vitalício", loading: "A redirecionar para Stripe…",
    or: "ou", promo: "Tenho um código promo", cvs: "CVs", cv: "CV",
  },
};

const fmtPrice = (amount, currency) => `$${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
const fmtPerCV = (amount, cvs) => `$${(amount / cvs / 100).toFixed(2)}`;

const PACK_ICONS = {
  aussiecv_pack_starter: FileText,
  aussiecv_pack_backpacker: Package,
  aussiecv_pack_season: Star,
};

// Which pack we highlight as MOST POPULAR
const HIGHLIGHT_LOOKUP = "aussiecv_pack_backpacker";

export default function UpgradeModal({ open, onClose, lang = "en", onOpenRedeem }) {
  const t = L[lang] || L.en;
  const [packs, setPacks] = useState([]);
  const [premium, setPremium] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/payments/plans`);
        setPacks(r.data.packs || []);
        setPremium((r.data.premium || [])[0] || null);
      } catch {
        setError("Could not load plans");
      }
    })();
  }, [open]);

  const buy = async (lookup_key) => {
    setBusy(lookup_key);
    setError("");
    try {
      const r = await axios.post(
        `${API}/payments/checkout`,
        { lookup_key, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  // Sort packs by CV count ascending (Starter → Backpacker → Season)
  const sortedPacks = useMemo(() => [...packs].sort((a, b) => a.cvs - b.cvs), [packs]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[110] flex items-start md:items-center justify-center p-3 md:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="upgrade-modal"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl my-3 md:my-6"
            initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-[#292524] via-[#3d3730] to-[#2A5C5A] text-white px-5 md:px-6 py-4 md:py-5 flex items-start justify-between gap-3 rounded-t-3xl">
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E5A93D] text-[#292524] text-[11px] font-bold mb-2">
                  <Crown className="w-3 h-3" /> AUSSIECV
                </div>
                <div className="font-heading font-extrabold text-xl md:text-3xl leading-tight">{t.title}</div>
                <div className="text-white/70 text-xs md:text-sm mt-1">{t.sub}</div>
              </div>
              <button onClick={onClose} className="text-white/70 hover:text-white flex-shrink-0" data-testid="upgrade-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Packs section */}
            <div className="px-5 md:px-6 pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-heading font-bold text-[#292524] text-sm uppercase tracking-wide">{t.packs_title}</div>
                <div className="text-[10px] text-[#78716C] hidden sm:block">{t.features_packs}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                {sortedPacks.map((p) => {
                  const Icon = PACK_ICONS[p.lookup_key] || FileText;
                  const isHighlight = p.lookup_key === HIGHLIGHT_LOOKUP;
                  return (
                    <div
                      key={p.lookup_key}
                      className={`relative rounded-2xl p-4 md:p-5 flex flex-col ${
                        isHighlight
                          ? "border-2 border-[#D95D39] bg-gradient-to-br from-[#FFF6F0] to-white"
                          : "border-2 border-[#EAE5DB] bg-white"
                      }`}
                      data-testid={`pack-${p.lookup_key}`}
                    >
                      {isHighlight && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-0.5 rounded-full bg-[#D95D39] text-white text-[10px] font-bold">
                          {t.best_value}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`w-4 h-4 ${isHighlight ? "text-[#D95D39]" : "text-[#78716C]"}`} />
                        <div className="font-bold text-[#292524]">
                          {p.cvs} {p.cvs > 1 ? t.cvs : t.cv}
                        </div>
                      </div>
                      <div className="font-heading font-extrabold text-3xl md:text-4xl text-[#292524]">
                        {fmtPrice(p.amount, p.currency)}
                      </div>
                      <div className="text-[11px] text-[#78716C] mb-4">
                        {fmtPerCV(p.amount, p.cvs)} {t.per_cv}
                      </div>
                      <button
                        onClick={() => buy(p.lookup_key)}
                        disabled={!!busy}
                        className={`w-full mt-auto ${isHighlight ? "btn-primary" : "btn-outline"}`}
                        data-testid={`pack-${p.lookup_key}-btn`}
                      >
                        {busy === p.lookup_key ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> {t.loading}</>
                        ) : (
                          <>{t.buy}</>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#78716C] mt-2 sm:hidden">{t.features_packs}</div>
            </div>

            {/* Lifetime section */}
            {premium && (
              <div className="px-5 md:px-6 py-5 mt-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading font-bold text-[#292524] text-sm uppercase tracking-wide">{t.lifetime_title}</div>
                </div>
                <div className="border-2 border-[#E5A93D] bg-gradient-to-br from-[#FDF7E6] to-white rounded-2xl p-5 relative" data-testid="plan-lifetime">
                  <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full bg-[#E5A93D] text-[#292524] text-[10px] font-bold">
                    {t.lifetime_badge}
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Inf className="w-4 h-4 text-[#E5A93D]" />
                        <div className="font-bold text-[#292524]">{t.lifetime_title}</div>
                      </div>
                      <div className="font-heading font-extrabold text-3xl text-[#292524]">
                        {fmtPrice(premium.amount, premium.currency)} <span className="text-sm text-[#78716C] font-medium">· {t.one_time}</span>
                      </div>
                      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#57534E]">
                        {t.features_lifetime.map((f, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <Check className="w-3.5 h-3.5 text-[#3B8268] flex-shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      onClick={() => buy(premium.lookup_key)}
                      disabled={!!busy}
                      className="btn-primary md:w-56"
                      data-testid="plan-lifetime-btn"
                    >
                      {busy === premium.lookup_key ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {t.loading}</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> {t.go_lifetime}</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && <div className="px-6 pb-3 text-sm text-[#D9423E]" data-testid="upgrade-error">{error}</div>}

            {onOpenRedeem && (
              <div className="border-t border-[#F2EBE1] px-6 py-3 text-center text-xs text-[#78716C] rounded-b-3xl">
                {t.or}{" "}
                <button onClick={() => { onClose(); onOpenRedeem(); }} className="text-[#D95D39] font-semibold hover:underline" data-testid="upgrade-redeem-link">
                  {t.promo}
                </button>
              </div>
            )}
            {!onOpenRedeem && <div className="pb-4 rounded-b-3xl" />}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
